import type { SellerPriceHistorySeries } from "./deal-offers-types";
import { buildOfferHistoryComparison } from "./offer-history-comparison";
import type { OfferHistoryComparisonPoint } from "./offer-history-comparison";
import { priceSummary } from "./offer-history-window";

export function buildOfferHistoryChartModel({
  series,
  offerId,
  checkedAt,
  listingIntervals,
}: {
  series: SellerPriceHistorySeries[];
  offerId: string;
  checkedAt: Date | string | null;
  listingIntervals?: SellerPriceHistorySeries["intervals"];
}) {
  const seller = series.find((item) => item.sellerOfferId === offerId);
  const observationTimes = (listingIntervals ?? seller?.intervals ?? [])
    .map((interval) => new Date(interval.fromAt).getTime())
    .filter(Number.isFinite);
  const points =
    listingIntervals === undefined
      ? legacySellerPoints(series, offerId, checkedAt, observationTimes)
      : buildOfferHistoryComparison(series, offerId, checkedAt, listingIntervals);
  const selectedStats = priceSummary(points, "selected");
  return {
    points,
    observationTimes,
    hasListingHistory: listingIntervals !== undefined,
    showSelected:
      listingIntervals !== undefined &&
      points.some((point) => point.selected !== null) &&
      points.some((point) => point.selected !== point.minimum),
    selectedStats,
    ...priceSummary(points, "minimum"),
  };
}

function legacySellerPoints(
  series: SellerPriceHistorySeries[],
  offerId: string,
  checkedAt: Date | string | null,
  observationTimes: number[],
): OfferHistoryComparisonPoint[] {
  if (!observationTimes.length) return [];
  const seller = series.find((item) => item.sellerOfferId === offerId)!;
  const firstTime = Math.min(...observationTimes);
  const sellerTimes = seller.intervals
    .flatMap((interval) => [interval.fromAt, interval.toAt])
    .filter((time) => time !== null)
    .map((time) => new Date(time).getTime())
    .filter(Number.isFinite);
  const checkedTime = checkedAt === null ? firstTime : new Date(checkedAt).getTime();
  const endTime = Math.max(...sellerTimes, Number.isFinite(checkedTime) ? checkedTime : firstTime);
  const boundedSeries = series.map((item) => ({
    ...item,
    intervals: item.intervals
      .filter((interval) => new Date(interval.fromAt).getTime() <= endTime)
      .map((interval) => {
        const end = interval.toAt === null ? Infinity : new Date(interval.toAt).getTime();
        return end > endTime ? { ...interval, toAt: new Date(endTime) } : interval;
      }),
  }));
  return buildOfferHistoryComparison(boundedSeries, offerId, new Date(endTime))
    .filter((point) => point.time >= firstTime && point.time <= endTime)
    .map((point) => ({ ...point, minimum: point.selected }));
}
